import {
    Features,
    useFeatureFlagValue,
} from '@experiences/feature-flags';
import type { ILabelModelObject } from '@experiences/interfaces';
import {
    OrchestratorObjectType,
    PermissionType,
} from '@experiences/interfaces';
import { GlobalStyles } from '@experiences/theme';
import { ArrowRight } from '@mui/icons-material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import {
    IconButton,
    Link,
    MenuItem,
    Tooltip,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import createStyles from '@mui/styles/createStyles';
import type { History } from 'history';
import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';

import * as RouteNames from '../../../../common/constants/RouteNames';
import UiMenu from '../../../common/UiMenu/UiMenu';
import UiNestedMenuItem from '../../../common/UiMenu/UiNestedMenuItem';
import { hasPermission } from '../../addedit/properties/TenantTagsUtil';
import { redirectToOrchestrator } from '../../common/redirectToOrchestrator.default';
import { useTagsDisabled } from '../../common/useTagsDisabled';

const useStyles = makeStyles(theme => ({
    ...GlobalStyles(theme),
    ...createStyles({
        deleteButton: { color: theme.palette.semantic.colorErrorText },
        menuItem: { '&:focus-visible': { backgroundColor: theme.palette.semantic.colorHover } },
    }),
}));

const TenantTagsLabelsMoreActions: React.FC<{
    logEvent: (name: string, details?: any) => Promise<void>;
    getRoute: (route: string, customAccountName?: string | undefined) => string;
    history: History;
    row: ILabelModelObject;
    organizationName: string;
    tenantId: string;
    tenantName?: string;
    permissions?: PermissionType[];
    onDelete: (rows: ILabelModelObject) => Promise<void>;
}> = ({
    logEvent,
    getRoute,
    history,
    row,
    organizationName,
    tenantId,
    tenantName,
    permissions,
    onDelete,
}) => {
    const { formatMessage: translate } = useIntl();
    const classes = useStyles();
    const { disabledToolTipMessage } = useTagsDisabled();
    const EnablePortalTagActionCenterNavigation = useFeatureFlagValue(Features.EnablePortalTagActionCenterNavigation.name);

    const orchestratorCount = row.entityReferencesCount?.reduce((acc, curr) => {
        if (OrchestratorObjectType.includes(curr.entityType)) {
            return acc + curr.count;
        }
        return acc;
    }, 0) ?? 0;
    const actionCount = row.entityReferencesCount?.reduce((acc, curr) => {
        if (!OrchestratorObjectType.includes(curr.entityType)) {
            return acc + curr.count;
        }
        return acc;
    }, 0) ?? 0;

    const nestedMenu = useMemo(() => {
        const options = [
            <Tooltip
                tabIndex={0}
                placement='right'
                key='listTaggedOrchestratorObjects'
                title={translate({ id: orchestratorCount === 0 ? 'CLIENT_NO_TAGGED_OBJECTS_IN_ORCHESTRATOR' : 'CLIENT_LIST_TAGGED_OBJECTS_IN_ORCHESTRATOR' })}>
                <div className={classes.menuItem}>
                    <MenuItem
                        tabIndex={0}
                        component={Link}
                        underline='none'
                        disabled={orchestratorCount === 0}
                        href={redirectToOrchestrator(
                            organizationName,
                            tenantName,
                            {
                                tags: {
                                    data: row,
                                    expandable: false,
                                    id: row?.name?.toLowerCase() ?? '',
                                    text: row?.name,
                                },
                            })}
                        target='_blank'
                        onClick={() => {
                            logEvent('PortalTagManagement.ListTaggedObjectsLabels');
                        }}
                        data-cy='ap-grid-row-list-tagged-orchestrator-objects'
                    >
                        {translate({ id: 'CLIENT_ORCHESTRATOR' })}
                    </MenuItem>
                </div>
            </Tooltip>,
        ];
        if (EnablePortalTagActionCenterNavigation) {
            options.push(
                <Tooltip
                    tabIndex={0}
                    placement='right'
                    key='listTaggedActionObjects'
                    title={translate({ id: actionCount === 0 ? 'CLIENT_NO_TAGGED_OBJECTS_IN_ACTION' : 'CLIENT_LIST_TAGGED_OBJECTS_IN_ACTION' })}>
                    <div className={classes.menuItem}>
                        <MenuItem
                            tabIndex={0}
                            component={Link}
                            underline='none'
                            disabled={actionCount === 0}
                            href={`${window.location.origin}/${organizationName}/${tenantName}/actions_`}
                            target='_blank'
                            data-cy='ap-grid-row-list-tagged-action-objects'
                        >
                            {translate({ id: 'CLIENT_ECOMMERCE_ACTION_CENTER' })}
                        </MenuItem>
                    </div>
                </Tooltip>
            );
        }
        return options;
    }, [
        EnablePortalTagActionCenterNavigation,
        actionCount,
        classes.menuItem,
        logEvent,
        orchestratorCount,
        organizationName,
        row,
        tenantName,
        translate,
    ]);

    return (<UiMenu
        trigger={<Tooltip
            title={translate({ id: 'CLIENT_SHOW_MORE_ACTIONS' })}
            placement='right'>
            <IconButton
                aria-label={translate({ id: 'CLIENT_SHOW_MORE_ACTIONS' })}
                data-cy="ap-data-grid-show-more-actions-button"
            >
                <MoreVertIcon className={classes.icon} />
            </IconButton>
        </Tooltip>}
        menu={[
            <UiNestedMenuItem
                leftAnchorMenus
                key='listTaggedObjects'
                label={translate({ id: 'CLIENT_LIST_TAGGED_OBJECTS_SIMPLE' })}
                righticon={<ArrowRight />}
                data-cy='ap-grid-row-list-tagged-objects'
                menu={nestedMenu} />,
            <Tooltip
                tabIndex={0}
                placement='right'
                key="editLabel-tooltip"
                title={!hasPermission(permissions, [ PermissionType.View, PermissionType.Edit ])
                    ? disabledToolTipMessage(permissions, [ PermissionType.View, PermissionType.Edit ])
                    : undefined}
            >
                <div className={classes.menuItem}>
                    <MenuItem
                        key='editLabel'
                        disabled={!hasPermission(permissions, [ PermissionType.View, PermissionType.Edit ])}
                        onClick={() => history.push(getRoute(process.buildConfigs.showForMSI
                            ? `${RouteNames.TenantTagsLabelsMsi}/edit/${row?.key}`
                            : `${RouteNames.TenantTagsLabels}/edit/${row?.key}`.replace(':tenantId', tenantId)))}
                        data-cy='ap-grid-row-edit'
                    >
                        {translate({ id: 'CLIENT_EDIT' })}
                    </MenuItem>
                </div>
            </Tooltip>,
            <Tooltip
                tabIndex={0}
                placement='right'
                key="editLabel-tooltip"
                title={!hasPermission(permissions, [ PermissionType.View, PermissionType.Delete ])
                    ? disabledToolTipMessage(permissions, [ PermissionType.View, PermissionType.Delete ])
                    : undefined}
            >
                <div className={classes.menuItem}>
                    <MenuItem
                        className={classes.deleteButton}
                        key='deleteLabel'
                        disabled={!hasPermission(permissions, [ PermissionType.View, PermissionType.Delete ])}
                        onClick={() => onDelete(row)}
                        data-cy='ap-grid-row-delete'
                    >
                        {translate({ id: 'CLIENT_DELETE' })}
                    </MenuItem>
                </div>
            </Tooltip>,
        ]} />);
};
export default TenantTagsLabelsMoreActions;
